/**
 * Discord Channel Adapter
 *
 * Uses Discord Gateway WebSocket and REST API
 */

import { WebSocket } from "ws";
import {
	BaseChannelAdapter,
	globalChannelRegistry,
	type ChannelConfig,
	type ChannelProfile,
} from "./base.js";
import type { InboundMessage, OutboundMessage, SenderInfo } from "../types/index.js";

/** Discord channel configuration */
export interface DiscordConfig {
	/** Bot token */
	botToken: string;
	/** Application ID */
	applicationId: string;
	/** Intents (default: basic + messages + guilds) */
	intents?: number;
}

/** Discord Gateway intents */
export const DiscordIntents = {
	GUILDS: 1 << 0,
	GUILD_MEMBERS: 1 << 1,
	GUILD_MODERATION: 1 << 2,
	GUILD_EMOJIS_AND_STICKERS: 1 << 3,
	GUILD_INTEGRATIONS: 1 << 4,
	GUILD_WEBHOOKS: 1 << 5,
	GUILD_INVITES: 1 << 6,
	GUILD_VOICE_STATES: 1 << 7,
	GUILD_PRESENCES: 1 << 8,
	GUILD_MESSAGES: 1 << 9,
	GUILD_MESSAGE_REACTIONS: 1 << 10,
	GUILD_MESSAGE_TYPING: 1 << 11,
	DIRECT_MESSAGES: 1 << 12,
	DIRECT_MESSAGE_REACTIONS: 1 << 13,
	DIRECT_MESSAGE_TYPING: 1 << 14,
	MESSAGE_CONTENT: 1 << 15,
} as const;

/** Discord adapter */
export class DiscordAdapter extends BaseChannelAdapter {
	readonly channelType = "discord";

	private botToken: string;
	private applicationId: string;
	private intents: number;
	private ws?: WebSocket;
	private sessionId?: string;
	private sequenceNumber?: number;
	private heartbeatInterval?: NodeJS.Timeout;
	private botInfo?: DiscordUser;
	private resumeGatewayUrl?: string;

	constructor(config: ChannelConfig) {
		super(config);
		const discordConfig = config.config as unknown as DiscordConfig;
		this.botToken = discordConfig.botToken;
		this.applicationId = discordConfig.applicationId;
		// Default intents: GUILDS + GUILD_MESSAGES + DIRECT_MESSAGES + MESSAGE_CONTENT
		this.intents = discordConfig.intents ?? 0b11111111110111;
	}

	/** Initialize the adapter */
	async initialize(): Promise<void> {
		// Get bot info
		const response = await this.apiCall<DiscordUser>("users/@me");
		this.botInfo = response;
		console.log(`Discord bot initialized: ${this.botInfo?.username}#${this.botInfo?.discriminator}`);

		// Connect to Gateway
		await this.connectGateway();
	}

	/** Shutdown the adapter */
	async shutdown(): Promise<void> {
		this.connected = false;
		if (this.heartbeatInterval) {
			clearInterval(this.heartbeatInterval);
		}
		this.ws?.close();
	}

	/** Send a message to Discord */
	async send(message: OutboundMessage): Promise<void> {
		const channelId = message.target;

		for (const part of message.parts) {
			switch (part.type) {
				case "text": {
					// Discord max message length is 2000
					const chunks = this.chunkText(part.text, 2000);
					for (const chunk of chunks) {
						await this.apiCall(`channels/${channelId}/messages`, {
							method: "POST",
							body: {
								content: chunk,
								message_reference: message.replyTo
									? { message_id: message.replyTo }
									: undefined,
							},
						});
					}
					break;
				}

				case "typing": {
					// Send typing indicator
					await this.apiCall(`channels/${channelId}/typing`, { method: "POST" });
					break;
				}

				case "reaction": {
					if (part.messageId) {
						// URL encode emoji for API
						const emoji = encodeURIComponent(part.emoji);
						await this.apiCall(
							`channels/${channelId}/messages/${part.messageId}/reactions/${emoji}/@me`,
							{ method: "PUT" },
						);
					}
					break;
				}

				// Images and files require multipart/form-data, skip for now
			}
		}
	}

	/** Get channel profile */
	async getProfile(): Promise<ChannelProfile> {
		return {
			type: this.channelType,
			id: this.botInfo?.id ?? "unknown",
			name: this.botInfo?.username ?? "Discord Bot",
			username: this.botInfo?.username,
			connected: this.connected,
			capabilities: {
				text: true,
				images: true,
				files: true,
				reactions: true,
				threads: true,
				editMessages: true,
				deleteMessages: true,
				typing: true,
				cards: true, // Via embeds
				voice: false, // Bot can join but not implemented here
				maxMessageLength: 2000,
				maxFileSize: 25 * 1024 * 1024, // 25MB
			},
		};
	}

	/** Connect to Discord Gateway */
	private async connectGateway(): Promise<void> {
		// Get gateway URL
		const gatewayInfo = await this.apiCall<DiscordGatewayInfo>("gateway/bot");
		const gatewayUrl = this.resumeGatewayUrl ?? `${gatewayInfo?.url ?? "wss://gateway.discord.gg"}?v=10&encoding=json`;

		console.log(`Connecting to Discord Gateway: ${gatewayUrl}`);

		this.ws = new WebSocket(gatewayUrl);

		this.ws.on("open", () => {
			console.log("Discord Gateway connected");
		});

		this.ws.on("message", (data: Buffer) => {
			try {
				const payload = JSON.parse(data.toString());
				this.handleGatewayPayload(payload);
			} catch (error) {
				console.error("Failed to parse Discord payload:", error);
			}
		});

		this.ws.on("close", (code: number) => {
			console.log(`Discord Gateway closed: ${code}`);
			this.connected = false;
			if (code !== 1000) {
				// Attempt to reconnect
				setTimeout(() => this.connectGateway(), 5000);
			}
		});

		this.ws.on("error", (error: Error) => {
			console.error("Discord Gateway error:", error);
			this.emitError(error);
		});
	}

	/** Handle Gateway payload */
	private handleGatewayPayload(payload: DiscordPayload): void {
		// Update sequence number
		if (payload.s !== undefined) {
			this.sequenceNumber = payload.s;
		}

		switch (payload.op) {
			case 10: // Hello
				this.handleHello(payload.d);
				break;

			case 11: // Heartbeat ACK
				// Heartbeat acknowledged
				break;

			case 0: // Dispatch
				if (payload.t) {
					this.handleDispatch(payload.t, payload.d);
				}
				break;

			case 1: // Heartbeat
				this.sendHeartbeat();
				break;

			case 7: // Reconnect
				console.log("Discord requested reconnect");
				this.ws?.close(1000);
				break;

			case 9: // Invalid Session
				console.log("Discord session invalid, re-identifying");
				this.sessionId = undefined;
				setTimeout(() => this.identify(), 5000);
				break;
		}
	}

	/** Handle Hello opcode */
	private handleHello(data: unknown): void {
		const helloData = data as { heartbeat_interval: number };
		// Start heartbeat
		this.heartbeatInterval = setInterval(() => {
			this.sendHeartbeat();
		}, helloData.heartbeat_interval);

		// Identify or resume
		if (this.sessionId) {
			this.resume();
		} else {
			this.identify();
		}
	}

	/** Send heartbeat */
	private sendHeartbeat(): void {
		if (this.ws?.readyState === WebSocket.OPEN) {
			this.ws.send(JSON.stringify({
				op: 1,
				d: this.sequenceNumber ?? null,
			}));
		}
	}

	/** Send identify payload */
	private identify(): void {
		console.log("Identifying with Discord Gateway");
		this.ws?.send(JSON.stringify({
			op: 2,
			d: {
				token: this.botToken,
				intents: this.intents,
				properties: {
					os: process.platform,
					browser: "Piagnet",
					device: "Piagnet",
				},
			},
		}));
	}

	/** Send resume payload */
	private resume(): void {
		if (!this.sessionId) {
			console.log("No session to resume, identifying instead");
			this.identify();
			return;
		}
		console.log("Resuming Discord session");
		this.ws?.send(JSON.stringify({
			op: 6,
			d: {
				token: this.botToken,
				session_id: this.sessionId,
				seq: this.sequenceNumber,
			},
		}));
	}

	/** Handle dispatch event */
	private handleDispatch(eventType: string, data: unknown): void {
		switch (eventType) {
			case "READY":
				this.handleReady(data as DiscordReady);
				break;

			case "RESUMED":
				console.log("Discord session resumed");
				this.connected = true;
				break;

			case "MESSAGE_CREATE":
				this.handleMessageCreate(data as DiscordMessage);
				break;

			case "INTERACTION_CREATE":
				// Handle slash commands
				break;
		}
	}

	/** Handle Ready event */
	private handleReady(data: DiscordReady): void {
		console.log(`Discord ready: ${data.user.username}#${data.user.discriminator}`);
		this.sessionId = data.session_id;
		this.resumeGatewayUrl = data.resume_gateway_url;
		this.connected = true;
	}

	/** Handle message create event */
	private handleMessageCreate(message: DiscordMessage): void {
		// Ignore own messages
		if (message.author.id === this.botInfo?.id) return;

		// Ignore bots
		if (message.author.bot) return;

		// Determine scope
		const scope = message.guild_id ? "group" : "dm";

		// Check access control
		if (scope === "group" && this.config.access.groupPolicy === "mention") {
			const isMentioned = this.isMentioned(message);
			if (!isMentioned) return;
		}

		// Build sender info
		const sender: SenderInfo = {
			id: message.author.id,
			name: message.author.global_name ?? message.author.username,
			username: message.author.username,
			avatar: message.author.avatar
				? `https://cdn.discordapp.com/avatars/${message.author.id}/${message.author.avatar}.png`
				: undefined,
			isAdmin: false, // Would need guild member fetch
			guildId: message.guild_id,
		};

		// Build content
		let content: InboundMessage["content"];

		if (message.content) {
			// Check for commands
			if (message.content.startsWith("/")) {
				const [command, ...args] = message.content.slice(1).split(" ");
				content = {
					type: "command",
					command,
					args,
				};
			} else {
				content = {
					type: "text",
					text: this.cleanMentions(message.content),
				};
			}
		} else {
			return; // Ignore empty messages for now
		}

		// Get agent ID from binding
		const agentId = this.config.name;

		// Create inbound message
		const inboundMessage = this.createInboundMessage({
			id: message.id,
			agentId,
			scope,
			identifier: message.channel_id,
			sender,
			content,
			replyTo: message.referenced_message?.id,
		});

		this.emitMessage(inboundMessage);
	}

	/** Check if bot is mentioned */
	private isMentioned(message: DiscordMessage): boolean {
		return message.mentions?.some((m) => m.id === this.botInfo?.id) ?? false;
	}

	/** Clean mentions from text */
	private cleanMentions(text: string): string {
		// Replace <@USER_ID> with @username or remove
		return text.replace(/<@!?\d+>/g, "").trim();
	}

	/** Make API call to Discord */
	private async apiCall<T = unknown>(
		endpoint: string,
		options: { method?: string; body?: unknown } = {},
	): Promise<T> {
		const url = `https://discord.com/api/v10/${endpoint}`;

		const response = await fetch(url, {
			method: options.method ?? "GET",
			headers: {
				Authorization: `Bot ${this.botToken}`,
				"Content-Type": "application/json",
			},
			body: options.body ? JSON.stringify(options.body) : undefined,
		});

		if (!response.ok) {
			const error = await response.text();
			throw new Error(`Discord API error: ${response.status} ${error}`);
		}

		// Handle 204 No Content
		if (response.status === 204) {
			return undefined as T;
		}

		return response.json() as T;
	}
}

/** Register with global registry */
globalChannelRegistry.register("discord", DiscordAdapter);

/** Discord API types */
interface DiscordPayload {
	op: number;
	d?: unknown;
	s?: number;
	t?: string;
}

interface DiscordUser {
	id: string;
	username: string;
	discriminator: string;
	global_name?: string;
	avatar?: string;
	bot?: boolean;
}

interface DiscordMessage {
	id: string;
	channel_id: string;
	guild_id?: string;
	author: DiscordUser;
	content: string;
	timestamp: string;
	mentions: DiscordUser[];
	referenced_message?: DiscordMessage;
}

interface DiscordReady {
	v: number;
	user: DiscordUser;
	guilds: unknown[];
	session_id: string;
	resume_gateway_url: string;
}

interface DiscordGatewayInfo {
	url: string;
}
